import { z } from 'zod';

import type { ArtifactKind } from './artifacts.ts';

/**
 * What produced one version, as `artifact_versions.reason` stores it.
 *
 * The store is the only writer and checks the value on the way in; a read answers `null` for a
 * value this build does not know. Text rather than a database enum, so a new reason is a line here
 * rather than a migration. Research questions, the guide and the audience carry one, and a Brief
 * version other than its publication does; see `assertVersionReason`.
 */
export const ARTIFACT_VERSION_REASONS = [
  'drafted_from_goal',
  'redrafted_from_changed_goal',
  'drafted_from_questions',
  'redrafted_from_changed_questions',
  'redrafted_for_coverage',
  'updated_for_changed_questions',
  'reworded_by_you',
  'reworded_at_your_request',
  'added_at_your_request',
  'removed_at_your_request',
  'reordered_at_your_request',
  'changed_at_your_request',
  'image_shown_at_your_request',
  'image_removed_at_your_request',
  'image_removed_from_materials',
  'drafted_from_guide',
  'updated_for_changed_guide',
  'reconciled',
  'hidden_by_you',
  'shown_by_you',
  'reordered_by_you',
  'revised_by_sigora',
  'updated_from_follow_up',
] as const;
export const artifactVersionReasonSchema = z.enum(ARTIFACT_VERSION_REASONS);
export type ArtifactVersionReason = z.infer<typeof artifactVersionReasonSchema>;

/** A stored reason, or `null` for none and for one this build does not know. */
export const parseArtifactVersionReason = (value: unknown): ArtifactVersionReason | null => {
  const parsed = artifactVersionReasonSchema.safeParse(value);

  return parsed.success ? parsed.data : null;
};

/**
 * What produced a Brief version after its publication: one framing edit by the researcher, or a
 * revision by Sigora — on its own, or because the researcher chose to update the Brief from a
 * follow-up question's answer.
 */
export const BRIEF_VERSION_REASONS = [
  'reworded_by_you',
  'hidden_by_you',
  'shown_by_you',
  'reordered_by_you',
  'revised_by_sigora',
  'updated_from_follow_up',
] as const satisfies readonly ArtifactVersionReason[];
export type BriefVersionReason = (typeof BRIEF_VERSION_REASONS)[number];

/** The Brief reasons that say Sigora revised it, which the blocks a revision changed are badged from. */
export const BRIEF_REVISION_REASONS = [
  'revised_by_sigora',
  'updated_from_follow_up',
] as const satisfies readonly BriefVersionReason[];
export type BriefRevisionReason = (typeof BRIEF_REVISION_REASONS)[number];

/** Whether a version's reason says Sigora revised the Brief (`BRIEF_REVISION_REASONS`). */
export const isBriefRevisionReason = (reason: string | null): reason is BriefRevisionReason =>
  (BRIEF_REVISION_REASONS as readonly (string | null)[]).includes(reason);

/** The kinds whose every version says what produced it. */
const KINDS_WITH_REASONS: ReadonlySet<ArtifactKind> = new Set(['rqs', 'dg', 'audience']);

/**
 * Whether a version of this kind may be written with this reason; `first` says whether it is the
 * artifact's first version.
 *
 * Every research-question, guide and audience version says what produced it, because each step's version
 * line and history are read off it. A Brief's publication — its first version — says nothing, and
 * every later Brief version says, with a Brief's reason, which framing edit or revision wrote it.
 * Every other kind carries none. A mismatch is a caller of the store getting it wrong, never
 * anything a request sent, so it throws as our bug.
 */
export const assertVersionReason = (
  kind: ArtifactKind,
  reason: ArtifactVersionReason | null,
  { first }: { first: boolean },
): void => {
  if (KINDS_WITH_REASONS.has(kind) && reason === null) {
    throw new Error(`a ${kind} version must say what produced it`);
  }

  if (kind === 'brief') {
    if (reason !== null && !(BRIEF_VERSION_REASONS as readonly string[]).includes(reason)) {
      throw new Error(`a brief version cannot say it was ${reason}`);
    }
    if (first !== (reason === null)) {
      throw new Error(
        first
          ? `a brief's publication says nothing, and was given ${String(reason)}`
          : 'a brief version after its publication must say what produced it',
      );
    }
    return;
  }

  if (!KINDS_WITH_REASONS.has(kind) && reason !== null) {
    throw new Error(`a ${kind} version carries no reason, and was given ${reason}`);
  }
};
